import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext.jsx';

/**
 * Route guard. Wraps any path that requires an authenticated admin.
 *
 *   'restoring' → renders a brief blank (avoids a one-frame flash of
 *                  the login page before /me lands)
 *   'authed'    → renders children
 *   'guest'     → <Navigate> to /login, remembering where they were
 *                  going (in location.state.from) so a successful
 *                  login lands them on the page they originally
 *                  requested
 *
 * Optionally accepts `requireRole` — e.g. <ProtectedRoute requireRole="SUPERADMIN">
 * gates a section to Superadmins only. The user lands on /forbidden
 * if they're authenticated but under-roled. (TODO: build that page;
 * for now they get a plain bounce home.)
 *
 * @author <lukman@zshift.io>
 */
export function ProtectedRoute({ children, requireRole }) {
  const { status, me } = useAdminAuth();
  const location = useLocation();

  if (status === 'restoring') {
    // Brief blank during token validation — prevents login-flash.
    return <div style={{ minHeight: '100vh' }} />;
  }

  if (status !== 'authed') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireRole && me?.role !== requireRole) {
    return <Navigate to="/" replace />;
  }

  return children;
}
